
import { storage } from '../lib/storage';
import { Order, PayoutRequest, Wallet, Store } from '../types';

export const financeService = {
  calculateBalances: (storeId: string) => {
    const orders = storage.getOrders().filter(o => o.storeId === storeId);
    
    // In a real DB, these would be aggregated. 
    // Here we calculate on the fly for the sandbox experience.
    const pendingBalance = orders
      .filter(o => o.status !== 'CANCELLED' && !o.settledAt)
      .reduce((sum, o) => sum + (o.sellerNet || 0), 0);
      
    const settledBalance = orders
      .filter(o => !!o.settledAt)
      .reduce((sum, o) => sum + (o.sellerNet || 0), 0);

    const payouts = storage.getPayoutRequests().filter(p => p.storeId === storeId && p.status === 'PAID');
    const totalWithdrawn = payouts.reduce((sum, p) => sum + p.amount, 0);

    return {
      pendingBalance,
      availableBalance: settledBalance - totalWithdrawn,
      totalWithdrawn
    };
  },

  requestPayout: async (storeId: string, amount: number) => {
    const balances = financeService.calculateBalances(storeId);
    const store = storage.getStoreConfig(storeId);

    if (amount > balances.availableBalance) {
      throw new Error("Insufficient available balance.");
    }

    if (!store.payoutMethod) {
      throw new Error("Please add a payout method before requesting a withdrawal.");
    }

    const requests = storage.getPayoutRequests();
    const newRequest: PayoutRequest = {
      id: 'PAY-' + Math.random().toString(36).substr(2, 9),
      storeId,
      amount,
      status: 'PENDING',
      requestedAt: new Date().toISOString(),
      payoutMethodSnapshot: store.payoutMethod
    };

    storage.savePayoutRequests([newRequest, ...requests]);
    return newRequest;
  },

  processPayout: async (requestId: string, status: 'PAID' | 'REJECTED') => {
    const requests = storage.getPayoutRequests();
    const updated = requests.map(r => {
      if (r.id === requestId) {
        return {
          ...r,
          status,
          processedAt: new Date().toISOString()
        };
      }
      return r;
    });
    storage.savePayoutRequests(updated);
    return updated.find(r => r.id === requestId);
  },

  triggerSettlements: () => {
    const orders = storage.getOrders();
    const config = storage.getPlatformConfig();
    const now = new Date();
    
    let updatedCount = 0;
    const updatedOrders = orders.map(o => {
      if (o.status === 'DELIVERED' && !o.settledAt) {
        const deliveredAt = new Date(o.createdAt); // Mocking delivery time as same as creation for sandbox
        const diffDays = Math.ceil((now.getTime() - deliveredAt.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diffDays >= config.settlementDays) {
          updatedCount++;
          return { ...o, settledAt: now.toISOString() };
        }
      }
      return o;
    });

    if (updatedCount > 0) {
      storage.saveOrders(updatedOrders);
    }
    return updatedCount;
  }
};
